import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { debounce } from "../../../common/snippet/Debouncing";
import ClientInput from "../../../common/element/Input/clientInput";
import Heading from "../../../common/element/Heading";
import ThreeDotBouncingLoader from "../../../common/element/ButtonLoader/ThreeDotBouncingLoder";
import { setBreadcrumbs } from "../../../redux/Admin/breadcrumbsSlice";
import { getAssessmentResults, clearAssessmentResults } from "../../../redux/AssessmentResults/assessmentResultsSlice";
import {
  selectAssessmentResultsList,
  selectAssessmentResultsLoading,
  selectAssessmentResultsError,
} from "../../../redux/AssessmentResults/assessmentResultsSelector";
import { Pagination } from "../../../common/pagination";
import ModernPagination from "../../../common/pagination/ModernPagination";
import { formatToolName } from "../../../common/Formatters.js/formatters";
import ToolManagmentCard from "../../../common/element/Card/ToolManagmnetCard";

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const capitalizeFirstLetter = (str) => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

const AssessmentCardContent = ({ item }) => {
  const toolId = item?.tool_id;
  const resultJson = item?.result_json || {};

  const isNCodesResult = toolId === 1 || toolId === 2 || toolId === 3;
  const isInnerDriveResult = toolId === 4;
  const isHumanNeedsResult = toolId === 8;
  const isBaselineResult = toolId === 5;
  const isPersonalityTestResult = toolId === 6;
  const isTraitsTestResult = toolId === 7;

  const personalityTestRows = useMemo(() => {
    if (!isPersonalityTestResult) return [];
    return Object.entries(resultJson)
      .filter(([key]) => key !== 'user_ordered_items')
      .map(([key, value]) => ({ key, value }))
      .sort((a, b) => b.value - a.value);
  }, [isPersonalityTestResult, resultJson]);

  const highestPersonalityScore = personalityTestRows.length > 0 ? personalityTestRows[0]?.value : 0;

  const personalityAssessmentType = useMemo(() => {
    if (!isPersonalityTestResult || !resultJson?.user_ordered_items?.assessment_type) return [];
    return resultJson.user_ordered_items.assessment_type.map((name) => ({
      name,
      score: resultJson[name] ?? 0,
    }));
  }, [isPersonalityTestResult, resultJson]);

  const personalityCoachType = useMemo(() => {
    if (!isPersonalityTestResult || !resultJson?.user_ordered_items?.coach_type) return [];
    return resultJson.user_ordered_items.coach_type.map((name) => ({
      name,
      score: resultJson[name] ?? 0,
    }));
  }, [isPersonalityTestResult, resultJson]);

  const innerDriveAssessmentRows = useMemo(() => {
    if (!isInnerDriveResult || !resultJson?.user_ordered_items?.top_assessment) return [];
    return resultJson.user_ordered_items.top_assessment.map((name) => ({
      name,
      score: resultJson[name] ?? 0,
    }));
  }, [isInnerDriveResult, resultJson]);

  const innerDriveCoachRows = useMemo(() => {
    if (!isInnerDriveResult || !resultJson?.user_ordered_items?.top_after_coach) return [];
    return resultJson.user_ordered_items.top_after_coach.map((name) => ({
      name,
      score: resultJson[name] ?? 0,
    }));
  }, [isInnerDriveResult, resultJson]);

  const innerDriveFallbackRows = useMemo(() => {
    if (!isInnerDriveResult) return [];
    return [
      { key: "Certainty", value: resultJson?.Certainty || 0 },
      { key: "Variety", value: resultJson?.Variety || 0 },
      { key: "Significance", value: resultJson?.Significance || 0 },
      { key: "Love/Connection", value: resultJson?.["Love/Connection"] || 0 },
      { key: "Growth", value: resultJson?.Growth || 0 },
      { key: "Contribution", value: resultJson?.Contribution || 0 },
    ].sort((a, b) => b.value - a.value);
  }, [isInnerDriveResult, resultJson]);

  const humanNeedsRows = useMemo(() => {
    if (!isHumanNeedsResult) return [];
    return [
      { key: "Certainty", value: resultJson?.Certainty || 0 },
      { key: "Variety", value: resultJson?.Variety || 0 },
      { key: "Significance", value: resultJson?.Significance || 0 },
      { key: "Love/Connection", value: resultJson?.["Love/Connection"] || 0 },
      { key: "Growth", value: resultJson?.Growth || 0 },
      { key: "Contribution", value: resultJson?.Contribution || 0 },
    ].sort((a, b) => b.value - a.value);
  }, [isHumanNeedsResult, resultJson]);

  const traitsTestRows = useMemo(() => {
    if (!isTraitsTestResult) return [];
    const traitNames = Object.keys(resultJson)
      .filter((key) => key.endsWith("Level"))
      .map((key) => key.replace("Level", ""));
    return traitNames
      .map((trait) => ({
        name: trait.charAt(0).toUpperCase() + trait.slice(1),
        level: resultJson[`${trait}Level`] || "",
        score: resultJson[`${trait}Score`] || 0,
      }))
      .sort((a, b) => b.score - a.score);
  }, [isTraitsTestResult, resultJson]);

  const ncodesRows = useMemo(() => {
    if (!isNCodesResult) return [];
    return resultJson?.selected_codes || [];
  }, [isNCodesResult, resultJson]);

  const baselineSelectedQuestions = useMemo(() => {
    if (!isBaselineResult) return [];
    return resultJson?.selected_questions || [];
  }, [isBaselineResult, resultJson]);

  const baselineUserOrderedItems = useMemo(() => {
    if (!isBaselineResult) return [];
    return resultJson?.user_ordered_items || [];
  }, [isBaselineResult, resultJson]);

  // Tool ID 6: Personality Test
  if (isPersonalityTestResult) {
    return (
      <div className="space-y-4 p-1">
        {personalityAssessmentType.length === 0 && personalityTestRows.length > 0 && (
          <table className="w-full">
            <thead>
              <tr className="bg-[#FFF4E5]">
                <th className="py-2 px-3 text-left font-lato font-medium text-sm text-[#475467]">S No.</th>
                <th className="py-2 px-3 text-left font-lato font-medium text-sm text-[#475467]">Personality Type</th>
                <th className="py-2 px-3 text-left font-lato font-medium text-sm text-[#475467]">Your Score</th>
              </tr>
            </thead>
            <tbody className="text-start">
              {personalityTestRows.map((row, index) => (
                <tr key={index} className="border-t border-gray-100">
                  <td className={`py-2 px-3 font-lato text-sm ${row.value === highestPersonalityScore ? "text-orange-600 font-semibold" : "text-[#1D2939]"}`}>
                    {String(index + 1).padStart(2, "0")}
                  </td>
                  <td className={`py-2 px-3 font-lato text-sm ${row.value === highestPersonalityScore ? "text-orange-600 font-semibold" : "text-[#1D2939]"}`}>
                    {row.key}
                  </td>
                  <td className={`py-2 px-3 font-lato text-sm ${row.value === highestPersonalityScore ? "text-orange-600" : "text-[#1D2939]"}`}>
                    {row.value}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {personalityAssessmentType.length > 0 && (
          <div>
            <h4 className="font-lato font-semibold text-[13px] leading-[150%] text-[#101828] mb-2 text-start">
              Personality Type – As Per Assessment:
            </h4>
            <table className="w-full border border-gray-200 rounded-lg overflow-hidden">
              <thead>
                <tr className="bg-[#FFF4E5]">
                  <th className="py-2 px-3 text-left font-lato font-medium text-sm text-[#475467]">Rank</th>
                  <th className="py-2 px-3 text-left font-lato font-medium text-sm text-[#475467]">Personality Type</th>
                  <th className="py-2 px-3 text-left font-lato font-medium text-sm text-[#475467]">Score</th>
                </tr>
              </thead>
              <tbody className="text-start">
                {personalityAssessmentType.map((row, index) => (
                  <tr key={index} className="border-t border-gray-100">
                    <td className="py-2 px-3 font-lato text-sm text-[#1D2939]">{index + 1}</td>
                    <td className="py-2 px-3 font-lato text-sm text-[#1D2939]">{row.name}</td>
                    <td className="py-2 px-3 font-lato text-sm text-[#1D2939]">{String(row.score).padStart(2, '0')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {personalityCoachType.length > 0 && (
          <div>
            <h4 className="font-lato font-semibold text-[13px] leading-[150%] text-[#101828] mb-2 text-start">
              Personality Type – After Discussion With Coach:
            </h4>
            <table className="w-full border border-gray-200 rounded-lg overflow-hidden">
              <thead>
                <tr className="bg-[#FFF4E5]">
                  <th className="py-2 px-3 text-left font-lato font-medium text-sm text-[#475467]">Rank</th>
                  <th className="py-2 px-3 text-left font-lato font-medium text-sm text-[#475467]">Personality Type</th>
                  <th className="py-2 px-3 text-left font-lato font-medium text-sm text-[#475467]">Score</th>
                </tr>
              </thead>
              <tbody className="text-start">
                {personalityCoachType.map((row, index) => (
                  <tr key={index} className="border-t border-gray-100">
                    <td className="py-2 px-3 font-lato text-sm text-[#1D2939]">{index + 1}</td>
                    <td className="py-2 px-3 font-lato text-sm text-[#1D2939]">{row.name}</td>
                    <td className="py-2 px-3 font-lato text-sm text-[#1D2939]">{String(row.score).padStart(2, '0')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  // Tool ID 4: Inner Drive Assessment
  if (isInnerDriveResult) {
    if (innerDriveAssessmentRows.length > 0) {
      return (
        <div className="space-y-4 p-1">
          <div>
            <h4 className="font-lato font-semibold text-[13px] leading-[150%] text-[#101828] mb-2 text-start">
              Top 2 Inner Drives – As Per Assessment:
            </h4>
            <table className="w-full border border-gray-200 rounded-lg overflow-hidden">
              <thead>
                <tr className="bg-[#FFF4E5]">
                  <th className="py-2 px-3 text-left font-lato font-medium text-sm text-[#475467]">Rank</th>
                  <th className="py-2 px-3 text-left font-lato font-medium text-sm text-[#475467]">Inner Drive</th>
                  <th className="py-2 px-3 text-left font-lato font-medium text-sm text-[#475467]">Score</th>
                </tr>
              </thead>
              <tbody className="text-start">
                {innerDriveAssessmentRows.map((row, index) => (
                  <tr key={row.name} className="border-t border-gray-100">
                    <td className="py-2 px-3 font-lato text-sm text-[#1D2939]">{index + 1}</td>
                    <td className="py-2 px-3 font-lato text-sm text-[#1D2939]">{row.name}</td>
                    <td className="py-2 px-3 font-lato text-sm text-[#1D2939]">{String(row.score).padStart(2, '0')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {innerDriveCoachRows.length > 0 && (
            <div>
              <h4 className="font-lato font-semibold text-[13px] leading-[150%] text-[#101828] mb-2 text-start">
                Final Top 2 Inner Drives – After Discussion With Coach:
              </h4>
              <table className="w-full border border-gray-200 rounded-lg overflow-hidden">
                <thead>
                  <tr className="bg-[#FFF4E5]">
                    <th className="py-2 px-3 text-left font-lato font-medium text-sm text-[#475467]">Rank</th>
                    <th className="py-2 px-3 text-left font-lato font-medium text-sm text-[#475467]">Inner Drive</th>
                    <th className="py-2 px-3 text-left font-lato font-medium text-sm text-[#475467]">Score</th>
                  </tr>
                </thead>
                <tbody className="text-start">
                  {innerDriveCoachRows.map((row, index) => (
                    <tr key={row.name} className="border-t border-gray-100">
                      <td className="py-2 px-3 font-lato text-sm text-[#1D2939]">{index + 1}</td>
                      <td className="py-2 px-3 font-lato text-sm text-[#1D2939]">{row.name}</td>
                      <td className="py-2 px-3 font-lato text-sm text-[#1D2939]">{String(row.score).padStart(2, '0')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      );
    }
    return (
      <table className="w-full">
        <thead>
          <tr className="bg-[#FFF4E5]">
            <th className="py-2 px-3 text-left font-lato font-medium text-sm text-[#475467]">S No.</th>
            <th className="py-2 px-3 text-left font-lato font-medium text-sm text-[#475467]">Personality Type</th>
            <th className="py-2 px-3 text-left font-lato font-medium text-sm text-[#475467]">Score</th>
          </tr>
        </thead>
        <tbody className="text-start">
          {innerDriveFallbackRows.map((row, index) => (
            <tr key={row.key} className="border-t border-gray-100">
              <td className="py-2 px-3 font-lato text-sm text-[#1D2939]">{String(index + 1).padStart(2, "0")}</td>
              <td className="py-2 px-3 font-lato text-sm text-[#1D2939]">{row.key}</td>
              <td className="py-2 px-3 font-lato text-sm text-[#1D2939]">{row.value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  // Tool ID 8: Human Needs
  if (isHumanNeedsResult) {
    return (
      <table className="w-full">
        <thead>
          <tr className="bg-[#FFF4E5]">
            <th className="py-2 px-3 text-left font-lato font-medium text-sm text-[#475467]">S No.</th>
            <th className="py-2 px-3 text-left font-lato font-medium text-sm text-[#475467]">Personality Type</th>
            <th className="py-2 px-3 text-left font-lato font-medium text-sm text-[#475467]">Score</th>
          </tr>
        </thead>
        <tbody className="text-start">
          {humanNeedsRows.map((row, index) => (
            <tr key={row.key} className="border-t border-gray-100">
              <td className="py-2 px-3 font-lato text-sm text-[#1D2939]">{String(index + 1).padStart(2, "0")}</td>
              <td className="py-2 px-3 font-lato text-sm text-[#1D2939]">{row.key}</td>
              <td className="py-2 px-3 font-lato text-sm text-[#1D2939]">{row.value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  // Tool ID 7: Traits Test
  if (isTraitsTestResult) {
    return (
      <table className="w-full">
        <thead>
          <tr className="bg-[#FFF4E5]">
            <th className="py-2 px-3 text-left font-lato font-medium text-sm text-[#475467]">S No.</th>
            <th className="py-2 px-3 text-left font-lato font-medium text-sm text-[#475467]">Trait</th>
            <th className="py-2 px-3 text-left font-lato font-medium text-sm text-[#475467]">Level</th>
            <th className="py-2 px-3 text-left font-lato font-medium text-sm text-[#475467]">Score</th>
          </tr>
        </thead>
        <tbody className="text-start">
          {traitsTestRows.map((row, index) => (
            <tr key={index} className="border-t border-gray-100">
              <td className="py-2 px-3 font-lato text-sm text-[#1D2939]">
                {String(index + 1).padStart(2, "0")}
              </td>
              <td className="py-2 px-3 font-lato text-sm text-[#1D2939]">{row.name}</td>
              <td className="py-2 px-3 font-lato text-sm text-[#1D2939]">
                {capitalizeFirstLetter(row.level)}
              </td>
              <td className="py-2 px-3 font-lato text-sm text-[#1D2939]">{row.score}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  // Tool ID 1, 2, 3: N-Codes
  if (isNCodesResult) {
    return (
      <table className="w-full">
        <thead>
          <tr className="bg-[#FFF4E5]">
            <th className="py-2 px-3 text-left font-lato font-medium text-sm text-[#475467]">N-Code</th>
            <th className="py-2 px-3 text-left font-lato font-medium text-sm text-[#475467]">E-Code</th>
          </tr>
        </thead>
        <tbody className="text-start">
          {ncodesRows.map((row, index) => (
            <tr key={row.id || index} className="border-t border-gray-100">
              <td className="py-2 px-3 font-lato text-sm text-[#1D2939]">{row.ncode}</td>
              <td className="py-2 px-3 font-lato text-sm text-[#1D2939]">{row.ecode}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  // Tool ID 5: Baseline
  if (isBaselineResult) {
    return (
      <div className="grid grid-cols-2 gap-6 p-2">
        <div>
          <p className="font-lato font-semibold text-sm text-[#101828] mb-3 text-center">System Generated Baseline</p>
          <div className="flex flex-col gap-3">
            {baselineSelectedQuestions.map((q, index) => (
              <div
                key={q.id || index}
                className="bg-[#FF8E1C] rounded-lg px-4 py-2 flex items-center justify-center text-white text-center font-medium text-sm shadow-sm min-h-[42px]"
              >
                {index + 1}. {q.text}
              </div>
            ))}
          </div>
        </div>
        {baselineUserOrderedItems.length > 0 && (
          <div>
            <p className="font-lato font-semibold text-sm text-[#101828] mb-3 text-center">User Reordered Baseline</p>
            <div className="flex flex-col gap-3">
              {baselineUserOrderedItems.map((q, index) => (
                <div
                  key={q.id || index}
                  className="bg-[#FF8E1C] rounded-lg px-4 py-2 flex items-center justify-center text-white text-center font-medium text-sm shadow-sm min-h-[42px]"
                >
                  {index + 1}. {q.text}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="py-6 text-center text-sm text-gray-400">No results available</div>
  );
};

const AssessmentCard = ({ item }) => (
  <ToolManagmentCard> 
    <div className="flex flex-col gap-1">
      <div className="flex justify-between">
        <div className="text-start">
          <span className="font-lato font-medium text-[14px] leading-[150%] text-[#98A2B3]">Assessment Name</span>
          <p className="font-lato font-medium text-[14px] leading-[150%] text-[#1D2939]">{item?.assessment_name || "N/A"}</p>
        </div>
        <div className="text-start">
          <span className="font-lato font-medium text-[14px] leading-[150%] text-[#98A2B3]">Session Name</span>
          <p className="font-lato font-medium text-[14px] leading-[150%] text-[#1D2939]">{item?.session_name || "N/A"}</p>
        </div>
      </div>
      <div className="flex justify-between mt-1">
        <div className="text-start">
          <span className="font-lato font-medium text-[14px] leading-[150%] text-[#98A2B3]">Tool Name</span>
          <p className="font-lato font-medium text-[14px] leading-[150%] text-[#1D2939]">{item?.tool_name ? formatToolName(item.tool_name) : "N/A"}</p>
        </div>
        <div className="text-start">
          <span className="font-lato font-medium text-[14px] leading-[150%] text-[#98A2B3]">Assessment Date</span>
          <p className="font-lato font-medium text-[14px] leading-[150%] text-[#1D2939]">{formatDate(item?.completed_at)}</p>
        </div>
      </div>
    </div>

    <div className="overflow-hidden rounded-lg border border-gray-200 mt-2">
      <AssessmentCardContent item={item} />
    </div>
  </ToolManagmentCard>
);

const PAGE_LIMIT = 10;

const ResultsOfAssessments = () => {
  const dispatch = useDispatch();

  const assessmentResults = useSelector(selectAssessmentResultsList);
  const loading = useSelector(selectAssessmentResultsLoading);
  const error = useSelector(selectAssessmentResultsError);

  const [search, setSearch] = useState("");
  const [filterpage, setfilterpage] = useState(1);
  const [filterlimit, setfilterlimit] = useState(PAGE_LIMIT);
  const hasSetBreadcrumbs = useRef(false);

  useEffect(() => {
    if (!hasSetBreadcrumbs.current) {
      dispatch(setBreadcrumbs([{ label: "Results of Assessments", url: "/results-of-assessments", home: "/results-of-assessments" }]));
      hasSetBreadcrumbs.current = true;
    }
  }, [dispatch]);

  useEffect(() => {
    dispatch(getAssessmentResults({ search }));
    setfilterpage(1);
  }, [dispatch, search]);

  useEffect(() => {
    return () => {
      dispatch(clearAssessmentResults());
    };
  }, [dispatch]);

  const handleSearch = (e) => {
    setSearch(e.target.value.trim());
  };

  const inputchangehandler = useCallback(debounce(handleSearch), []);

  const allResults = Array.isArray(assessmentResults) ? assessmentResults : [];
  const totalCount = allResults.length;
  const totalPages = Math.ceil(totalCount / filterlimit);

  const paginatedResults = useMemo(() => {
    const start = (filterpage - 1) * filterlimit;
    return allResults.slice(start, start + filterlimit);
  }, [allResults, filterpage, filterlimit]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-between items-center mb-2">
        <Heading>Results of Assessments</Heading>
        <div className="input-wrapper">
          <div className="input-suffix-start left-3.5">
            <img src="/assets/icons-svg/search-lg.svg" alt="" className="h-5" />
          </div>
          <ClientInput
            type="text"
            className="input pl-10 w-80 input-shadow h-10 text-sm border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            placeholder="Search"
            inputchangehandler={inputchangehandler}
            name="search"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <ThreeDotBouncingLoader />
        </div>
      ) : error ? (
        <div className="flex justify-center items-center h-64">
          <p className="text-red-500">Error: {error}</p>
        </div>
      ) : paginatedResults.length > 0 ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {paginatedResults.map((item, index) => (
              <AssessmentCard key={item?.id ?? index} item={item} />
            ))}
          </div>

          <div className="flex justify-between items-center mt-3">
            <Pagination
              totalCount={totalCount}
              pageNumber={filterpage}
              displayPage={paginatedResults.length}
              UpdatePageLimit={setfilterlimit}
              UpdatePage={setfilterpage}
            />
            {totalCount > 0 && totalPages > 1 && (
              <ModernPagination
                totalPages={totalPages}
                currentPage={filterpage}
                onPageChange={setfilterpage}
              />
            )}
          </div>
        </>
      ) : (
        <div className="flex justify-center items-center h-64">
          <p className="text-gray-400 text-sm">No assessment results found</p>
        </div>
      )}
    </div>
  );
};

export default ResultsOfAssessments;
